import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import SwitchSelector from "react-switch-selector";
import { Scrollbar } from "react-scrollbars-custom";
import { useAccessToken } from "../../../common/hooks/useAccessToken";
import { showLoader, hideLoader } from "../../../store/common/slice";
import { GAEvent } from "../../../common/google_analytics/google_analytics";
import { isDemoAccount } from "../../../common/shared/statelessMethods";
import { constants } from "../../../common/constants/constants";
import commentMock from "../../../common/mocks/demoAccount/he/managers/apartmentComments.json";
import BllinkUtils from "../../../common/utils/utils";
import Search from '../../../images/search.svg';
import AddComment from "../../comments/AddComment";
import { StyledNotes } from "./Notes.styled";

const BllinkTime = BllinkUtils.BllinkTime;
const BllinkFilter = BllinkUtils.Filter;

const subTabs = {
    manager: 'manager',
    bllink: 'bllink'
};

export function Notes({ buildingID, apartmentNumber }) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const client = useAccessToken();
    const currentBuildingData = useSelector(state => state.buildings.currentBuildingData);

    const [currentSubTab, setCurrentSubTab] = useState(subTabs.bllink);
    const [managerComments, setManagerComments] = useState([]);
    const [bllinkComments, setBllinkComments] = useState([]);
    const [filter, setFilter] = useState('');

    const ltr = useSelector(state => state.common.ltrEnabled);
    const userData = useSelector((state) => state.auth.user);

    useEffect(async () => {
        if (!client) return;

        dispatch(showLoader());
        await loadComments();
        dispatch(hideLoader()); 
    }, [buildingID, apartmentNumber, client]);

    const isDemoApartment = (buildingID, apartmentNumber) => isDemoAccount(buildingID) && ['1','2'].includes(apartmentNumber);

    async function loadComments() {
        let comments;
        if (isDemoApartment(buildingID, apartmentNumber)){
            comments = commentMock.comments;
        } else {
            const response = await client.managers.buildings.getComments(buildingID, apartmentNumber) ?? {comments: []};
            comments = response.comments;
        }
        setManagerComments(comments?.filter(item => !item.createdBy.includes('@bllink.co')) || []);
        setBllinkComments(comments?.filter(item => item.createdBy.includes('@bllink.co')));
    };

    async function createComment(text) {
        dispatch(showLoader());

        const apartmentId = currentBuildingData?.apartments.find(apt => apt.apartmentNumber === apartmentNumber)?.value;
        const response = await client.put(`/managers/comments`, {
            entityType: 'apartment',
            entityId: apartmentId,
            text: text
        });

        if (response.id) GAEvent(constants.GoogleAnalytics.Events.comments, 'commentAddedEvent', text, 'commentAddedSuccess');

        await loadComments();
        dispatch(hideLoader());
    };

    const isMyComment = owner => owner === userData.email;

    function renderComments(comment) {
        const showItem = BllinkFilter.filterItems([comment.text, comment.User?.firstName, comment.createdBy, BllinkTime.parseDate(comment.updatedAt)], filter);

        return (
            <>
                {showItem && (               
                    <li
                        key={comment.id}
                        className={`comment ${isMyComment(comment.createdBy) ? 'is-my' : ''}`}
                    >
                        <div className="data">
                            <span className="time">{new Date(comment.updatedAt).toLocaleTimeString()}</span>
                            <span className="date">
                                {BllinkTime.parseDate(comment.updatedAt)}
                            </span>
                            <span className="name">{isMyComment(comment.createdBy) ? t('comments.you') : (comment.User?.firstName || comment.createdBy)}</span>
                        </div>
                        <p>{comment.text}</p>
                    </li>             
                )}
            </>
        )
    };

    return (
        <StyledNotes ltr={ltr}>
            <div className="switch-wrapper">
                <SwitchSelector
                    initialSelectedIndex={1}
                    options={[
                        {
                            label: t('reports.managerNotes'),
                            value: subTabs.manager,
                            selectedBackgroundColor: '#E9E9FD',
                        },
                        {
                            label: t('reports.bllinkNotes'),
                            value: subTabs.bllink,
                            selectedBackgroundColor: '#E9E9FD',
                        }
                    ]}
                    selectedFontColor='#373647'
                    fontColor='rgba(55, 54, 71, 0.7);'
                    backgroundColor='transparent'
                    border='1px solid rgba(110, 116, 255, 0.2)'
                    onChange={e => setCurrentSubTab(e)}
                />
            </div>

            <div className="notes-wrapper">
                <div className="top-bar">
                    <div className="search">
                        <input
                            type="text"
                            name="search"
                            onChange={e => setFilter(e.target.value)}
                            placeholder={t('search.quick_search')}
                        />
                        <img src={Search} alt={t('search.search')} />
                    </div>
                </div>

                {currentSubTab === subTabs.manager && (
                    <>
                        <Scrollbar
                            style={{ width: '100%', height: 'calc(100vh - 526px)' }}
                            trackYProps={{
                                renderer: props => {
                                    const { elementRef, ...restProps } = props;
                                    return <div {...restProps} ref={elementRef} className="scroll-track" />;
                                }
                            }}
                            thumbYProps={{
                                renderer: props => {
                                    const { elementRef, ...restProps } = props;
                                    return <div {...restProps} ref={elementRef} className="scroll-thumb" />;
                                }
                            }}
                            rtl={false}
                            noScrollX
                        > 
                            <ul className='comments-list'>
                                {managerComments && managerComments.length > 0 ? 
                                    managerComments.map(comment => renderComments(comment)) :
                                    <p className="text-center mt-3">{t('comments.empty')}</p>
                                }
                            </ul>                      
                        </Scrollbar>

                        <AddComment
                            className="add-comment"
                            onEnter={createComment}
                        />              
                    </>
                )}

                {currentSubTab === subTabs.bllink && (
                    <Scrollbar
                        style={{ width: '100%', height: 'calc(100vh - 454px)' }}
                        trackYProps={{
                            renderer: props => {
                                const { elementRef, ...restProps } = props;
                                return <div {...restProps} ref={elementRef} className="scroll-track" />;
                            }
                        }}
                        thumbYProps={{
                            renderer: props => {
                                const { elementRef, ...restProps } = props;
                                return <div {...restProps} ref={elementRef} className="scroll-thumb" />;
                            }
                        }}
                        rtl={false}
                        noScrollX
                    >
                        <ul className='comments-list'>
                            {bllinkComments && bllinkComments.length > 0 ? 
                                bllinkComments.map(comment => renderComments(comment)) : 
                                <p className="text-center mt-3">{t('comments.empty')}</p>
                            }
                        </ul>         
                    </Scrollbar>
                )}
            </div>
        </StyledNotes>
    )
}
